// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';

// Services
import { StringService } from '../services/string.service';
import { CoreService } from '../../../../shared/services/core.service';
import { SpinnerService } from '../../../../shared/components/spinner/services/spinner.service';
import { DialogService } from '../../../../shared/services/dialog.service';

// Models
import { StringViewModel } from '../models/string-view.model';
import { AppCleanup } from '../../../../shared/models';
import { ConfirmDialogModel } from '../../../../shared/dialogs/confirm-dialog/confirm-dialog.component';

// Store
import { StringActions } from './string.action';

// Enums
import { StatusType } from '../../../../shared/enums';

// Helpers
import { UtilityHelper } from '../../../../shared/helpers';

export interface StringStateModel {
	items: StringViewModel[];
	loaded: boolean;
}

const defaultState: StringStateModel = {
	items: undefined,
	loaded: false,
};

@State<any>({
	name: 'string',
	defaults: defaultState,
})
@Injectable()
export class StringState extends AppCleanup {
	constructor(
		private stringService: StringService,
		private spinnerService: SpinnerService,
		private coreService: CoreService,
		private translateService: TranslateService,
		private dialogService: DialogService
	) {
		super();
	}

	@Action(StringActions.GetAll)
	getAll(ctx: StateContext<StringStateModel>, action: StringActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.stringService
			.getStrings()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (strings) => {
					ctx.setState({
						items: strings,
						loaded: true,
					});

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.STRING.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(StringActions.SetAll)
	setAll(ctx: StateContext<StringStateModel>, action: StringActions.SetAll) {
		ctx.setState({
			items: action.strings,
			loaded: true,
		});
	}

	@Action(StringActions.Create)
	create(ctx: StateContext<StringStateModel>, action: StringActions.Create) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.stringService
			.createString(action.data.string)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (string) => {
					ctx.setState(
						patch({
							items: append([string]),
						})
					);

					ctx.dispatch(new StringActions.CreateSuccess(string));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.CREATED', { name: this.translateService.instant('MODULE.STRING.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new StringActions.CreateError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_CREATED', { name: this.translateService.instant('MODULE.STRING.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(StringActions.Update)
	update(ctx: StateContext<StringStateModel>, action: StringActions.Update) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.stringService
			.updateString(action.data.string)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (string) => {
					const currentState = ctx.getState();
					const existingItem = currentState.items?.find((item) => item.id === string.id);

					if (existingItem) {
						ctx.setState(
							patch({
								items: updateItem((string) => string.id === action.data.string.id, action.data.string),
							})
						);
					}

					ctx.dispatch(new StringActions.UpdateSuccess(string));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.STRING.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new StringActions.UpdateError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.STRING.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(StringActions.Delete)
	delete(ctx: StateContext<StringStateModel>, action: StringActions.Delete) {
		let deleteString = () => {
			if (action.data.showSpinner) {
				this.spinnerService.showAppSpinner(); // Turns on app wide spinner
			}

			return this.stringService
				.deleteString(action.data.id)
				.pipe(takeUntil(this.destroy))
				.subscribe({
					next: () => {
						const currentState = ctx.getState();
						const existingItem = currentState.items?.find((item) => item.id === action.data.id);

						if (existingItem) {
							ctx.setState(
								patch({
									items: removeItem((string) => string.id === action.data.id),
								})
							);
						}

						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.DELETED', { name: this.translateService.instant('MODULE.STRING.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						}
					},
					error: () => {
						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.NOT_DELETED', { name: this.translateService.instant('MODULE.STRING.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						}
					},
				});
		};

		if (action.data.showConfirm) {
			const confirmModel: ConfirmDialogModel = {
				title: `${UtilityHelper.capitalizeFirstLetter(this.translateService.instant('ACTION.DELETE'))} ${this.translateService.instant('MODULE.STRING.TITLE')}`,
				message: `${this.translateService.instant('GENERIC.CONFIRM_DELETE', { name: this.translateService.instant('MODULE.STRING.TITLE') })}`,
				action: this.translateService.instant('ACTION.DELETE_CAPITAL'),
			};

			const dialogRef = this.dialogService.confirmAction(confirmModel);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe((result) => {
					if (result) {
						deleteString();
					}
				});
		} else {
			deleteString();
		}
	}

	@Selector()
	static isLoaded(state: StringStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static strings(state: StringStateModel): StringViewModel[] {
		return state.items;
	}
}
